/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Col, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { Button as AntButton, Modal, Checkbox, Divider, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Icon } from 'pnnl-react-core';

import tm30ExampleLibrary from 'data/TM30-Example-Library.json';
import { SPDxPlot } from 'components/plots';
import MetaDataForm from 'components/metaDataForm';
import MultipleCalc from 'components/multipleCalculator';
import EditAlphaOpicData from 'components/tabs/luox/editAlphaOpicData';
import { LoadingScreen } from 'components/loadingScreen';
import { getLuoxData, convertLuoxData } from 'utils';

import ReactDataSheet from 'react-datasheet';

import { setReportData, setLibraryData, setLuoxData } from 'redux/actions';

import '../index.scss';

const { Option } = Select;

const Examples = ({ view }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [addNewLibraryModal, setAddNewLibraryModal] = useState(false);
  // const [deleteUserLibraryModal, setDeleteUserLibraryModal] = useState(false);
  const [calculationLoader, setCalculationLoader] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const [exampleId, setExampleId] = useState('');
  const [userSpdId, setUserSpdId] = useState('');
  const [exampleOpen, setExampleOpen] = useState(false);
  const [spd, setSpd] = useState([]);
  const [wavelength, setWaveLength] = useState([]);
  const [grid, setGrid] = useState([]);
  const [metadata, setMetadata] = useState({
    manufacturer: '',
    model: '',
    description: '',
    creator: '',
    identifier: '',
    measurement: '',
    laboratory: '',
    report_number: '',
    report_date: '',
    creation_date: '',
    comments: '',
    spectral_quantity: '',
  });
  const [showLuoxModal, setShowLuoxModal] = useState(false);
  const [shouldCalculateLuoxData, setShouldCalculateLuoxData] = useState(false);

  useEffect(() => {
    setExampleOpen(false);
    setExampleId('');
    setUserSpdId('');
  }, []);

  const libraryData = useSelector((state) => state.dataReducers.libraryData);
  const luoxData = useSelector((state) => state.dataReducers.luoxData);
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);

  const handleLuoxCalculation = (e) => {
    const luox = e.target.checked;
    setShouldCalculateLuoxData(luox);
    if (luox) {
      const luoxData = getLuoxData(wavelength, spd);
      dispatch(setLuoxData(luoxData));
    } else {
      dispatch(setLuoxData({}));
    }
  };

  const handleSubmit = (submitType) => {
    const targetExampleLibraryData = tm30ExampleLibrary.find(
      (example) => example.id === exampleId
    );

    if (submitType === 'calculate') {
      let newReportData = {};
      newReportData = Object.assign({}, targetExampleLibraryData, {
        warnings: [],
      });
      if (shouldCalculateLuoxData) {
        newReportData = Object.assign({}, newReportData, {
          luoxData: luoxData,
        });
        dispatch(setReportData(newReportData));
      } else {
        dispatch(setReportData(newReportData));
      }
      history.push({
        pathname: '/results',
      });
    } else {
      let newLibraryData = {};
      newLibraryData = Object.assign({}, targetExampleLibraryData, {
        warnings: [],
      });
      if (shouldCalculateLuoxData) {
        // Standardizing area scale and power scales
        const libraryStandardLuoxUnits = {
          ...luoxUnits,
          ...{ areaScale: 'm2', powerScale: 'mW' },
        };
        const modifiedLuoxData = convertLuoxData(
          luoxData.luoxInput,
          libraryStandardLuoxUnits
        );
        if (shouldCalculateLuoxData) {
          newLibraryData = Object.assign({}, newLibraryData, {
            luoxData: {
              ...modifiedLuoxData,
              luoxUnits: libraryStandardLuoxUnits,
            },
          });
        }
        dispatch(setLibraryData([newLibraryData]));
      } else {
        dispatch(setLibraryData([newLibraryData]));
      }
      history.push({
        pathname: '/user-library',
      });
    }
  };

  const generateReport = () => {
    const targetUserLibraryData = libraryData.find(
      (data) => data.id === userSpdId
    );
    dispatch(setReportData(targetUserLibraryData));
    history.push('/results');
  };

  const handleChange = useCallback((value) => {
    const exampleId = value;
    const targetExampleLibraryData = tm30ExampleLibrary.find(
      (example) => example.id === exampleId
    );
    const wavelengths = targetExampleLibraryData.tm30.input.Wavelength;
    const spds = targetExampleLibraryData.tm30.input.SPD;

    // Generating grid items to populate table and graph
    let grid_items = [
      [
        { readOnly: true, value: 'Wavelength (nm)' },
        {
          readOnly: true,
          className: 'cell',
          value: 'Power',
        },
      ],
    ];
    wavelengths.forEach((wavelength, index) => {
      grid_items.push([
        {
          readOnly: true,
          value: wavelength,
          className: 'cell',
        },
        {
          value: spds[index],
        },
      ]);
    });

    setExampleId(exampleId);
    setUserSpdId('');
    setDisableButton(false);
    setExampleOpen(true);
    setSpd(spds);
    setWaveLength(wavelengths);
    setGrid(grid_items);
    setMetadata(targetExampleLibraryData.metadata);
  }, []);

  const handleUserSpdChange = useCallback((value) => {
    const userUserId = value;
    const targetUserLibraryData = libraryData.find(
      (data) => data.id === userUserId
    );
    const wavelengths = targetUserLibraryData.tm30.input.Wavelength;
    const spds = targetUserLibraryData.tm30.input.SPD;

    let grid_items = [
      [
        { readOnly: true, value: 'Wavelength (nm)' },
        {
          readOnly: true,
          className: 'cell',
          value: 'Power',
        },
      ],
    ];

    wavelengths.forEach((wavelength, index) => {
      grid_items.push([
        {
          readOnly: true,
          value: wavelength,
          className: 'cell',
        },
        {
          value: spds[index],
        },
      ]);
    });

    setSpd(spds);
    setWaveLength(wavelengths);
    setGrid(grid_items);
    setUserSpdId(userUserId);
    setExampleId('');
    setExampleOpen(true);
    setMetadata(targetUserLibraryData.metadata);
  }, []);

  const addNewModalContainer = (
    <Modal
      title='Add new SPD(s) to the User Library'
      visible={addNewLibraryModal}
      onCancel={() => setAddNewLibraryModal(false)}
      footer={[
        <AntButton key='close' onClick={() => setAddNewLibraryModal(false)}>
          Close
        </AntButton>,
      ]}
    >
      <MultipleCalc shouldClearOnCalculate />
    </Modal>
  );

  // const deleteUserLibraryModalContainer = (
  //   <Modal
  //     title="Clear User Library"
  //     visible={deleteUserLibraryModal}
  //     onCancel={() => setDeleteUserLibraryModal(false)}
  //     footer={[
  //       <AntButton key="close" onClick={() => setDeleteUserLibraryModal(false)}>
  //         Close
  //       </AntButton>,
  //       <AntButton
  //         key="clear"
  //         type="primary"
  //         danger
  //         onClick={() => {
  //           dispatch(clearLibraryData());
  //           setDeleteUserLibraryModal(false);
  //         }}
  //       >
  //         Clear
  //       </AntButton>,
  //     ]}
  //   >
  //     Are you sure you want clear the current user library?
  //   </Modal>
  // );

  const examplesLibraryInfo = (
    <Popover
      id='popover-trigger-hover-focus'
      title='Use of Example SPD Library'
    >
      These pre-loaded example SPDs are included for informational purposes
      only. They are not a statistical sample and their use as a research
      database is questionable.
    </Popover>
  );

  //////////// Example Library Dropdown ////////////

  const selectExampleSpd = useMemo(() => {
    return (
      <div>
        <h5>
          Example Library
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement='right'
            overlay={examplesLibraryInfo}
          >
            <span css={InfoIconStyle}>
              <Icon icon='info' />
            </span>
          </OverlayTrigger>
        </h5>
        <div>
          <Select
            showSearch
            css={DropDownStyle}
            placeholder='Choose a SPD'
            onChange={handleChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {tm30ExampleLibrary.map((example) => {
              const value = example.id;
              const label = example.id + ' - ' + example.metadata?.model;
              return <Option value={value}>{label}</Option>;
            })}
          </Select>
        </div>
      </div>
    );
  }, [tm30ExampleLibrary]);

  //////////// User Library Dropdown ////////////

  const selectSpdFromLibrary = useMemo(() => {
    return (
      <div>
        <h5>User Library</h5>
        {libraryData.length !== 0 ? (
          <div css={DropDownContainer}>
            <div>
              <Select
                css={DropDownStyle}
                placeholder='Choose a user SPD'
                onChange={handleUserSpdChange}
              >
                {libraryData.map((data) => {
                  const { identifier } = data.metadata;
                  const label = identifier ? identifier : 'Sample';
                  return <Option value={data.id}>{label}</Option>;
                })}
              </Select>
            </div>
            <Button
              onClick={() => setAddNewLibraryModal(true)}
              bsStyle='success'
            >
              Add new item(s)
            </Button>
          </div>
        ) : (
          <div>
            <Button
              onClick={() => setAddNewLibraryModal(true)}
              bsStyle='success'
            >
              Add new item(s)
            </Button>
          </div>
        )}
      </div>
    );
  }, [libraryData]);

  //////////// SPD output graph and table ////////////

  const outputSPDx = useMemo(() => {
    return (
      <div>
        <SPDxPlot inputType='library' spd={spd} wavelength={wavelength} />
        <div css={ReactDataSheetStyle}>
          <ReactDataSheet
            data={grid}
            valueRenderer={(cell) => cell.value}
            // onCellsChanged={changes => {
            //   changes.forEach(({ cell }) => {
            //     cell.readOnly = true;
            //     return cell;
            //   });
            // }}
            // onCellsChanged={changes => {
            //   const updatedGrid = grid.map(row => [...row]);
            //   changes.forEach(({ cell, row, col, value }) => {
            //     updatedGrid[row][col] = { ...updatedGrid[row][col], value };
            //   });
            //   setGrid(updatedGrid);
            // }}
          />
        </div>
      </div>
    );
  }, [spd, wavelength, grid]);

  return (
    <Fragment>
      <Col md={6}>
        {selectExampleSpd} {selectSpdFromLibrary} {addNewModalContainer}{' '}
        <Modal
          title='Edit Alpha-Opic Data'
          visible={showLuoxModal}
          onCancel={() => setShowLuoxModal(false)}
          footer={[
            <AntButton key='clear' onClick={() => setShowLuoxModal(false)}>
              Close
            </AntButton>,
          ]}
        >
          <EditAlphaOpicData measurementName={metadata.identifier} />
        </Modal>
      </Col>
      {view === 'examples' ? (
        <Col css={OutputColumnStyle(exampleOpen, calculationLoader)} md={5}>
          <Fragment>
            <div>
              <Col xs={6} css={MetaDataFormStyle}>
                <div>
                  <strong>Calculations to Perform:</strong>
                </div>
                <br />
                <Checkbox checked>IES TM-30 Color Rendition</Checkbox>
                <br />
                <Checkbox onChange={handleLuoxCalculation}>
                  CIE S026 Alpha-Opic{' '}
                </Checkbox>
                <Divider css={dividerStyle} />
                <div css={luoxDataContainerStyle(shouldCalculateLuoxData)}>
                  <EditAlphaOpicData measurementName={metadata.identifier} />
                </div>
                <MetaDataForm metadata={metadata} disabled={true} />
              </Col>
              <Col xs={6}>
                {outputSPDx}
                <div css={SpinnerStyle}>
                  <LoadingScreen show={calculationLoader} />
                </div>
                {userSpdId !== '' ? (
                  <div style={{ textAlign: 'center', margin: '18px 0' }}>
                    <Button onClick={generateReport} bsStyle='primary'>
                      Generate Reports
                    </Button>
                  </div>
                ) : (
                  <Fragment>
                    <br />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                      }}
                    >
                      <Button
                        style={{ marginRight: '1rem' }}
                        onClick={() => handleSubmit('calculate')}
                        bsStyle='primary'
                        type='submit'
                        disabled={disableButton}
                      >
                        Calculate
                      </Button>
                      <Button
                        bsStyle='success'
                        onClick={() => handleSubmit('AddToLibrary')}
                        disabled={disableButton}
                      >
                        Add to User Library
                      </Button>
                    </div>
                  </Fragment>
                )}
              </Col>
            </div>
          </Fragment>
        </Col>
      ) : null}
      {!exampleOpen && view === 'examples' && (
        <p css={EmptyOutputTextStyle}>
          Use one of the three options to the left (Call from Library, Import
          SPDX, or Paste Data) to initiate a calculation.
        </p>
      )}
    </Fragment>
  );
};

export default Examples;

Examples.propTypes = {
  view: PropTypes.string,
  libraryData: PropTypes.object,
  libraryInfo: PropTypes.array,
  metaData: PropTypes.object,
};

const DropDownContainer = css`
  // display: flex;
  // justify-content: space-between;
`;
const DropDownStyle = css`
  width: 28rem;
  margin-bottom: 2rem;
`;

const UserLibraryDeleteIcon = css`
  font-size: 18px;
  margin: 8px;
`;

const InfoIconStyle = css`
  font-size: 16px;
  color: #47494c;
  opacity: 0.7;
  margin-left: 0.2rem;
`;

const ReactDataSheetStyle = css`
  max-height: 41rem;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 2rem;

  table {
    width: 100%;
  }
`;

// const ActiveColumnArrowStyle = css`
//   position: absolute;
//   left: 44rem;
//   width: 100%;
//   svg {
//     font-size: 5rem;
//   }
// `;

const OutputColumnStyle = (exampleOpen, calculationLoader) => css`
  position: absolute;
  left: 47rem;
  top: -1rem;
  width: 68rem;
  height: 74rem;
  background: white;
  border-radius: 2rem;
  padding: 20px;
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.2);
  border: 1px solid #d9d9d9;
  opacity: ${calculationLoader ? '0.1' : '1'};

  // @media (max-width: 1200px) {
  //   width: 130%;
  // }

  ${!exampleOpen
    ? css`
        opacity: 0.2;
        pointer-events: none;
      `
    : ''};
`;

const MetaDataFormStyle = css`
  overflow-y: scroll;
  height: 68rem;
`;

const EmptyOutputTextStyle = css`
  position: absolute;
  left: 56rem;
  top: 18.8rem;
  width: 46rem;
  font-size: 20px;
  font-weight: bold;
`;

const SpinnerStyle = css`
  position: absolute;
  left: 25rem;
  top: 1rem;
`;

const luoxDataContainerStyle = (shouldCalculateLuoxData) => css`
  opacity: ${shouldCalculateLuoxData ? '1' : '0'};
  pointer-events: ${shouldCalculateLuoxData ? 'auto' : 'none'};
  height: ${shouldCalculateLuoxData ? '30rem' : '1rem'};

  transition: all 400ms;
`;

const dividerStyle = css`
  margin: 12px;
`;
